/**
 * TemplateEditorDialog — create / edit a custom email template.
 * Predefined (system) templates are read-only; users can clone them.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, Box, Typography, Button, TextField,
  CircularProgress, Alert, IconButton, Divider, Chip,
  Tabs, Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { CommSectionLabel } from './shared';

const getToken = () => localStorage.getItem('token') ?? '';

export interface EmailTemplate {
  template_id: string;
  business_id: string | null;
  template_name: string;
  template_key: string;
  subject_line: string;
  body_html: string;
  body_text: string | null;
  supported_merge_tags: string[] | null;
  is_predefined: boolean;
  created_at: string;
  updated_at: string;
}

interface Props {
  open: boolean;
  template: EmailTemplate | null;
  isNew: boolean;
  onSave: () => void;
  onClose: () => void;
}

const COMMON_MERGE_TAGS = [
  '{{business_name}}', '{{schedule_name}}', '{{asset_name}}', '{{asset_tag}}',
  '{{asset_type}}', '{{location_name}}', '{{user_name}}', '{{alert_level}}',
  '{{completion_pct}}', '{{overdue_by}}', '{{scheduled_time}}', '{{sent_at}}',
  '{{check_count_total}}', '{{check_count_completed}}', '{{check_count_pending}}',
];

const TemplateEditorDialog: React.FC<Props> = ({ open, template, isNew, onSave, onClose }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [error, setError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [statusSev, setStatusSev] = useState<'success' | 'error'>('success');

  const isPredefined = template?.is_predefined ?? false;
  const isReadOnly = !isNew && isPredefined;

  useEffect(() => {
    if (!open) return;
    setError(''); setStatusMsg(''); setPreviewHtml(''); setTabIndex(0);
    if (isNew || !template) {
      setName(''); setSubject(''); setBodyHtml('');
    } else {
      setName(template.template_name ?? '');
      setSubject(template.subject_line ?? '');
      setBodyHtml(template.body_html ?? '');
    }
  }, [open, isNew, template]);

  const handleSave = async () => {
    if (!name || !subject || !bodyHtml) {
      setError('Name, subject, and body are required.'); return;
    }
    setSaving(true); setError('');
    try {
      const payload = { template_name: name, subject_line: subject, body_html: bodyHtml };
      const headers = { Authorization: `Bearer ${getToken()}` };
      if (isNew) {
        await axios.post(`${API_BASE_URL}/communication-templates`, payload, { headers });
      } else {
        await axios.put(`${API_BASE_URL}/communication-templates/${template!.template_id}`, payload, { headers });
      }
      onSave();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!template?.template_id && !isNew) return;
    setPreviewing(true);
    try {
      if (template?.template_id) {
        const res = await axios.post(
          `${API_BASE_URL}/communication-templates/${template.template_id}/preview`,
          {},
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        setPreviewHtml(res.data.html ?? '');
        setPreviewSubject(res.data.subject ?? '');
      } else {
        // For new unsaved templates, render client-side (basic)
        setPreviewHtml(bodyHtml.replace(/\{\{(\w+)\}\}/g, '<em style="color:#6b7280">[$1]</em>'));
        setPreviewSubject(subject.replace(/\{\{(\w+)\}\}/g, '[$1]'));
      }
      setTabIndex(1);
    } catch {
      setError('Failed to generate preview.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleTest = async () => {
    if (!template?.template_id) return;
    setTesting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/communication-templates/${template.template_id}/send-test`,
        testEmail ? { recipient_email: testEmail } : {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setStatusMsg(`Test email sent to ${res.data?.sent_to ?? 'your account email'}.`);
      setStatusSev('success');
    } catch {
      setStatusMsg('Failed to send test email.');
      setStatusSev('error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '92vh' } }}
    >
      {/* Sticky header */}
      <Box sx={{
        px: 3, py: 2, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {isNew ? 'New Email Template' : isReadOnly ? 'View Template' : 'Edit Template'}
              </Typography>
              {isPredefined && (
                <Chip label="Predefined" size="small" color="default" variant="outlined" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {isReadOnly
                ? 'System templates cannot be edited — clone to create a custom version.'
                : 'Use {{merge_tags}} in subject and body to insert dynamic content.'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      {/* Tab bar */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0, px: 3 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Edit" />
          <Tab label="Preview" />
          {!isNew && !isReadOnly && <Tab label="Test" />}
        </Tabs>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {tabIndex === 0 && (
          <Box>
            <CommSectionLabel>Template Details</CommSectionLabel>
            <TextField
              label="Template Name"
              value={name}
              onChange={e => setName(e.target.value)}
              fullWidth
              disabled={isReadOnly}
              sx={{ mb: 2 }}
              autoFocus={isNew}
            />
            <TextField
              label="Subject Line"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              fullWidth
              disabled={isReadOnly}
              sx={{ mb: 2 }}
              helperText='Supports merge tags e.g. "Daily Check Report — {{business_name}}"'
            />

            <Divider sx={{ mb: 2 }} />

            <CommSectionLabel>Body HTML</CommSectionLabel>
            {!isReadOnly && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {COMMON_MERGE_TAGS.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    onClick={() => setBodyHtml(prev => prev + tag)}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'monospace' }}
                  />
                ))}
              </Box>
            )}
            <TextField
              multiline
              rows={16}
              value={bodyHtml}
              onChange={e => setBodyHtml(e.target.value)}
              fullWidth
              disabled={isReadOnly}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              placeholder={isReadOnly ? '' : '<p>Hello {{user_name}},</p>\n<p>Here is your check summary...</p>'}
            />
          </Box>
        )}

        {tabIndex === 1 && (
          <Box>
            {previewSubject && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Subject:</Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>{previewSubject}</Typography>
              </Box>
            )}
            {previewHtml ? (
              <Box
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Click "Preview" to render this template with sample data.
              </Typography>
            )}
          </Box>
        )}

        {tabIndex === 2 && !isNew && !isReadOnly && (
          <Box>
            <CommSectionLabel>Send Test Email</CommSectionLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Send a rendered version of this template with sample data to an email address.
            </Typography>
            {statusMsg && (
              <Alert severity={statusSev} onClose={() => setStatusMsg('')} sx={{ mb: 2 }}>
                {statusMsg}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField
                size="small"
                label="Recipient email"
                type="email"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="Leave blank for your account email"
                sx={{ flex: '1 1 260px' }}
              />
              <Button
                variant="outlined"
                startIcon={testing ? <CircularProgress size={14} /> : <SendIcon />}
                onClick={handleTest}
                disabled={testing}
              >
                {testing ? 'Sending…' : 'Send Test'}
              </Button>
            </Box>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>

      {/* Sticky footer */}
      <Box sx={{
        px: 3, py: 2, borderTop: 1, borderColor: 'divider',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 1.5, flexShrink: 0, flexWrap: 'wrap',
      }}>
        <Button
          variant="outlined"
          startIcon={previewing ? <CircularProgress size={14} /> : <PreviewIcon />}
          onClick={handlePreview}
          disabled={previewing}
        >
          {previewing ? 'Generating…' : 'Preview'}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          {!isReadOnly && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !name || !subject || !bodyHtml}
            >
              {saving ? 'Saving…' : isNew ? 'Create Template' : 'Save Changes'}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default TemplateEditorDialog;
